import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Datatable from "../../../core/common/dataTable";
import Api from "../../../core/data/redux/utils/apiCall";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { API_URLS } from "../../../core/data/redux/apis/urls";

const APARDownload = () => {
  const routes = all_routes;
  const dispatch = useDispatch();

  const [fromDate, setFromDate] = useState<Date | null>(new Date('2020-01-01'));
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [listData, setListData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const toISODate = (d: Date | null) => d ? d.toISOString().split('T')[0] + 'T00:00:00' : '';

  const parseDotNetDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const match = dateStr.match(/\/Date\((\d+)\)\//);
    if (match) {
      const timestamp = parseInt(match[1], 10);
      return new Date(timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    }
    return dateStr;
  };

  const fetchData = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both From and To dates.");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        fromDate: toISODate(fromDate),
        toDate: toISODate(toDate)
      };

      const response: any = await Api.post(API_URLS.APAR.APPRAISAL_DISPLAY, payload);

      const res = response?.data;
      let raw: any[] = [];
      if (Array.isArray(res?.data)) {
        raw = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        raw = res.data.data;
      } else if (Array.isArray(res)) {
        raw = res;
      }
      setListData(raw);
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong.");
      setListData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (item: any) => {
    const itemId = String(item?.id ?? item?.AppraisalID ?? '');
    setDownloadingId(itemId);
    try {
      const payload = {
        appraisalId: item?.AppraisalID ?? item?.appraisalId ?? item?.id,
        templateId: item?.templateId ?? item?.TemplateID ?? item?.appraisalTemplateName ?? item?.Template ?? "50062711"
      };

      const response: any = await Api.post(API_URLS.APAR.APPRAISAL_DOWNLOAD, payload);

      const res = response?.data;
      if (res) {
        let actualData = res;
        if (Array.isArray(res?.data)) {
          actualData = res.data[0];
        } else if (Array.isArray(res)) {
          actualData = res[0];
        }

        const contentStr = actualData?.Content ?? actualData?.content ?? actualData?.fileContent;
        const fileName = actualData?.FileName ?? actualData?.fileName ?? `APAR_${itemId}.pdf`;

        if (contentStr) {
          if (typeof contentStr === 'string' && contentStr.startsWith('http')) {
            window.open(contentStr, '_blank');
          } else {
            const link = document.createElement("a");
            link.href = `data:application/pdf;base64,${contentStr}`;
            link.download = fileName;
            link.click();
          }
        } else {
          toast.error("File content not found");
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const actionBodyTemplate = (rowData: any) => {
    const itemId = String(rowData?.id ?? rowData?.AppraisalID ?? '');
    const isDownloading = downloadingId === itemId;

    return (
      <button 
        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center" 
        onClick={() => handleDownload(rowData)}
        disabled={isDownloading}
        title="Download APAR"
      >
        {isDownloading ? (
          <><i className="ti ti-loader fa-spin me-1"></i> Downloading...</>
        ) : (
          <><i className="ti ti-download me-1"></i> Download</>
        )}
      </button>
    );
  };
  
  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 70,
      render: (text: any, record: any, index: number) => index + 1,
    },
    {
      title: "Employee No",
      dataIndex: "EmpID",
      sorter: (a: any, b: any) => (a.EmpID?.length || 0) - (b.EmpID?.length || 0),
    },
    {
      title: "Employee Name",
      dataIndex: "AppraiseeName",
      sorter: (a: any, b: any) => (a.AppraiseeName?.length || 0) - (b.AppraiseeName?.length || 0),
    },
    {
      title: "From Date",
      dataIndex: "FromDate",
      render: (text: any, record: any) => parseDotNetDate(record.FromDate),
      sorter: (a: any, b: any) => (a.FromDate?.length || 0) - (b.FromDate?.length || 0),
    },
    {
      title: "To Date",
      dataIndex: "ToDate",
      render: (text: any, record: any) => parseDotNetDate(record.ToDate),
      sorter: (a: any, b: any) => (a.ToDate?.length || 0) - (b.ToDate?.length || 0),
    },
    {
      title: "Template",
      dataIndex: "appraisalTemplateName",
      sorter: (a: any, b: any) => (a.appraisalTemplateName?.length || 0) - (b.appraisalTemplateName?.length || 0),
    },
    {
      title: "Action",
      render: (text: any, record: any) => actionBodyTemplate(record),
    }
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">APAR Download</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.apar.dashboard}>Employee Appraisal</Link>
                  </li>
                  <li className="breadcrumb-item active">APAR Download</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 module-card mb-3">
            <div className="card-body p-3">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="flex-grow-1" style={{ maxWidth: '250px' }}>
                  <label className="form-label fs-13 text-muted mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate ? toISODate(fromDate).split('T')[0] : ''}
                    onChange={(e) => setFromDate(e.target.value ? new Date(e.target.value) : null)}
                  />
                </div>
                <div className="flex-grow-1" style={{ maxWidth: '250px' }}>
                  <label className="form-label fs-13 text-muted mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate ? toISODate(toDate).split('T')[0] : ''}
                    onChange={(e) => setToDate(e.target.value ? new Date(e.target.value) : null)}
                  />
                </div>
                <div>
                  <button className="btn btn-primary btn-sm px-4" onClick={fetchData} disabled={isLoading}>
                    {isLoading ? <><i className="ti ti-loader fa-spin me-1"></i> Fetching...</> : "Display Data"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 module-card">
            <div className="card-body p-0 overflow-hidden">
              <Datatable columns={columns} dataSource={listData} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default APARDownload;
